"use client";
/**
 * WHICH CARD FAMILY DRAWS AN ATTACHED BLOCK — keyed by PILLAR.
 *
 * Ruled 2026-08-20: "kind: names the pillar." A materialised block's `kind:`
 * directive carries the pillar its results came from, so the pillar is what a
 * card is chosen by; the three-value render taxonomy this registry used to be
 * keyed on no longer discriminates a block at all (see the note on
 * `AttachedResultKind` itself).
 *
 * `Record<PillarCode, …>` is exhaustive by type. Twenty-three row-minting
 * pillars use the lossless parsed-item card: semantic codecs stay semantic and
 * the string-valued readers retain every exact field and repetition. Workouts
 * and Nutrition take DEDICATED cards, because their rows carry something a
 * fact list cannot serve — a demonstration a reader plays, and a nutrient panel
 * whose figures only mean anything under the serving they were measured
 * against. Weather is the only `null` decision because it cannot produce an
 * attached row. Unknown future pillars still take the
 * same lossless component through `attachedResultCardFor`.
 */
import type { ComponentType } from "react";
import type { ParsedAttachResults } from "@cabal/shared/domain/attached-result/index.js";
import { PillarCode } from "@cabal/shared/domain/session/orchestration/tool-protocol/follow-up-refs.js";
import { CattachedresultNutrition } from "./CattachedresultNutrition";
import { CattachedresultParsedItems } from "./CattachedresultParsedItems";
import { CattachedresultWorkouts } from "./CattachedresultWorkouts";

/** What every attach card receives: the block, read by the shared parser both
 *  sides use. No component parses a block itself. */
export type CattachedresultCard = ComponentType<{
  readonly block: ParsedAttachResults;
}>;

export const ATTACHED_RESULT_CARDS_BY_PILLAR: {
  readonly [P in PillarCode]: CattachedresultCard | null;
} = {
  // Travel & place.
  [PillarCode.Hotels]: CattachedresultParsedItems,
  [PillarCode.Flights]: CattachedresultParsedItems,
  [PillarCode.Places]: CattachedresultParsedItems,
  [PillarCode.Concerts]: CattachedresultParsedItems,
  [PillarCode.Weather]: null,
  // Commerce & property.
  [PillarCode.Products]: CattachedresultParsedItems,
  [PillarCode.Apps]: CattachedresultParsedItems,
  [PillarCode.RealEstate]: CattachedresultParsedItems,
  [PillarCode.Jobs]: CattachedresultParsedItems,
  [PillarCode.Finance]: CattachedresultParsedItems,
  // Knowledge.
  [PillarCode.Academic]: CattachedresultParsedItems,
  [PillarCode.Patents]: CattachedresultParsedItems,
  [PillarCode.Books]: CattachedresultParsedItems,
  [PillarCode.ClassicTexts]: CattachedresultParsedItems,
  [PillarCode.HistoricalDocuments]: CattachedresultParsedItems,
  [PillarCode.Scriptures]: CattachedresultParsedItems,
  [PillarCode.Knowledge]: CattachedresultParsedItems,
  // Body & training. Both take their own family: a movement is attached to be
  // WATCHED, and a food's figures are meaningless without the serving they were
  // measured against.
  [PillarCode.Workouts]: CattachedresultWorkouts,
  [PillarCode.Nutrition]: CattachedresultNutrition,
  // Media & social.
  [PillarCode.Youtube]: CattachedresultParsedItems,
  [PillarCode.MediaVisual]: CattachedresultParsedItems,
  [PillarCode.Music]: CattachedresultParsedItems,
  [PillarCode.Social]: CattachedresultParsedItems,
  // Web & current.
  [PillarCode.WebSearch]: CattachedresultParsedItems,
  [PillarCode.Answer]: CattachedresultParsedItems,
  [PillarCode.News]: CattachedresultParsedItems,
  [PillarCode.Trends]: CattachedresultParsedItems,
  [PillarCode.Github]: CattachedresultParsedItems,
};

/**
 * The card for one block's pillar — the generic item render when that pillar
 * has no dedicated card, and when the block names no pillar this build knows.
 *
 * The second case is the one the fallback exists for twice over: a block
 * persists longer than either deploy that reads it, so a name from a newer BL
 * (or from a hand that typed a word) arrives here as `null` and still draws.
 * Nothing about an unknown pillar is guessed at — it simply gets the render
 * that needs to know nothing about a pillar.
 */
export function attachedResultCardFor(input: {
  pillar: PillarCode | null;
}): CattachedresultCard {
  if (input.pillar === null) return CattachedresultParsedItems;
  return (
    ATTACHED_RESULT_CARDS_BY_PILLAR[input.pillar] ?? CattachedresultParsedItems
  );
}
